/**
 * Public landing — Google SSO on the main page (role comes from license redemption).
 * Copy uses t() + notranslate so the chosen language paints immediately (no Google Translate flash).
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { useLanguage } from '../contexts/LanguageContext.js';
import LanguageSelector from './LanguageSelector.js';

export default function LandingPortal({ authProviders, onGoogleSignIn }) {
  const { t } = useLanguage();

  const googleOn = Boolean(authProviders?.google?.enabled);
  const googleOAuthConfigured = Boolean(authProviders?.googleOAuthConfigured);
  const databaseConnected = Boolean(authProviders?.databaseConnected);

  const googleIcon = (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      aria-hidden
      sx={{ width: 18, height: 18, display: 'block' }}
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </Box>
  );

  return (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        background:
          'radial-gradient(circle at 20% 20%, rgba(81, 80, 255, 0.28), transparent 45%), radial-gradient(circle at 82% 12%, rgba(56, 218, 255, 0.22), transparent 40%), linear-gradient(145deg, #030812 0%, #0a1430 55%, #090d18 100%)'
      }}
    >
      <Box
        component="a"
        href="#landing-main"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          zIndex: 20000,
          px: 2,
          py: 1.25,
          bgcolor: '#0f172a',
          color: '#f8fafc',
          fontWeight: 700,
          fontSize: '0.95rem',
          textDecoration: 'none',
          borderRadius: '0 0 8px 0',
          border: '2px solid #f59e0b',
          '&:focus': { left: 0, outline: 'none' }
        }}
      >
        Skip to main content
      </Box>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(4, 8, 16, 0.88)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(120, 200, 255, 0.2)',
          color: 'text.primary'
        }}
      >
        <Toolbar sx={{ gap: 2, maxWidth: 1100, width: '100%', mx: 'auto', px: { xs: 2, sm: 3 } }}>
          <Typography component="h1" variant="h6" sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: '0.04em' }}>
            {t('landingBrandTitle')}
          </Typography>
          <Box dir="ltr" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, direction: 'ltr' }}>
            <LanguageSelector variant="inline" surface="dark" />
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        id="landing-main"
        tabIndex={-1}
        sx={{ flex: 1, maxWidth: 960, width: '100%', mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 }, outline: 'none' }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(18, 28, 47, 0.9)',
            borderRadius: 3,
            border: '1px solid rgba(120, 200, 255, 0.18)',
            boxShadow: '0 18px 52px rgba(0, 0, 0, 0.42), 0 0 32px rgba(34, 211, 238, 0.08)',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            minHeight: 320
          }}
        >
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'left'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'none',
                lineHeight: 1.45,
                m: 0
              }}
            >
              {t('landingWorkforceTraining')}
            </Typography>
            <Typography
              component="h2"
              variant="h4"
              sx={{ fontWeight: 800, color: 'text.primary', mt: 1, mb: 2, ml: 0, mr: 0, lineHeight: 1.2 }}
            >
              {t('landingHeadline')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.65, m: 0 }}>{t('landingBody')}</Typography>
          </Box>
          <Box
            sx={{
              background:
                'radial-gradient(circle at 50% 32%, rgba(34, 211, 238, 0.18), transparent 42%), linear-gradient(145deg, rgba(8, 18, 38, 0.95) 0%, rgba(3, 8, 18, 0.95) 100%)',
              borderLeft: { xs: 'none', md: '1px solid rgba(120, 200, 255, 0.16)' },
              p: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              textAlign: 'left'
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', m: 0, lineHeight: 1.7 }}>
              {t('landingAsideBlurb')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.25, mt: 2, width: '100%' }}>
              {googleOn ? (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={googleIcon}
                  onClick={onGoogleSignIn}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: '#24292f',
                    bgcolor: '#f6f8fa',
                    borderColor: 'rgba(27, 31, 36, 0.15)',
                    borderRadius: '6px',
                    px: 2.5,
                    py: 1.1,
                    justifyContent: 'center',
                    boxShadow: '0 1px 0 rgba(27, 31, 36, 0.04)',
                    '&:hover': {
                      bgcolor: '#f3f4f6',
                      borderColor: 'rgba(27, 31, 36, 0.15)',
                      boxShadow: '0 1px 0 rgba(27, 31, 36, 0.04)'
                    }
                  }}
                >
                  {t('landingGoogleCta')}
                </Button>
              ) : null}
              {!googleOn && !databaseConnected ? (
                <Typography variant="body2" color="text.secondary" role="alert" sx={{ m: 0 }}>
                  {t('landingHintDb')}
                </Typography>
              ) : null}
              {!googleOn && !googleOAuthConfigured ? (
                <Typography variant="body2" color="text.secondary" role="alert" sx={{ m: 0 }}>
                  {t('landingHintOAuth')}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Typography
          component="p"
          variant="body2"
          sx={{
            mt: 3,
            mb: 0,
            textAlign: 'center',
            color: 'text.secondary',
            lineHeight: 1.6
          }}
        >
          {t('landingContactQuestions')}{' '}
          <Box
            component="a"
            href="mailto:info@enterceptor.com"
            sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            info@enterceptor.com
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
